import { startOfDay } from 'date-fns';
import { convertDeliveryStatusToInternalName } from 'lib/constants';
import _, { get, unset } from 'lodash';
import { Device } from 'types';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { DeviceLookup } from 'views';

// For fixing inconsistencies
export const normalizeDeliveryTicket = (ticket: Ticket) => {
    const { plannedDate, dateDelivered, plannedGallons, gallonsDelivered } =
        ticket;
    const isDone =
        convertDeliveryStatusToInternalName[ticket.status] === 'done';

    const newTicket: Ticket = {
        ...ticket,
        status: convertDeliveryStatusToInternalName[ticket.status],
        ...(plannedDate
            ? { plannedDate: startOfDay(new Date(plannedDate)) }
            : {}),
        ...(isDone && dateDelivered
            ? { dateDelivered: startOfDay(new Date(dateDelivered)) }
            : { dateDelivered: undefined }),
        ...(plannedGallons ? { plannedGallons: Number(plannedGallons) } : {}),
        ...(isDone && gallonsDelivered
            ? { gallonsDelivered: Number(gallonsDelivered) }
            : { gallonsDelivered: undefined }),
    };

    // Some tickets have deliveredDate instead of (or in addition to) dateDelivered
    const deliveredDate = get(newTicket, 'deliveredDate');
    if (!!deliveredDate) {
        if (!newTicket.dateDelivered) {
            newTicket.dateDelivered = new Date(deliveredDate);
        }

        unset(newTicket, 'deliveredDate');
    } else if (!newTicket.dateDelivered && isDone) {
        newTicket.dateDelivered = new Date();
    }

    if (get(newTicket, 'deliveredGallons')) {
        if (!newTicket.gallonsDelivered) {
            newTicket.gallonsDelivered = Number(
                get(newTicket, 'deliveredGallons')
            );
        }

        unset(newTicket, 'deliveredGallons');
    } else if (!newTicket.gallonsDelivered && isDone) {
        newTicket.gallonsDelivered = 0;
    }

    if (!isDone) {
        delete newTicket.dateDelivered;
        delete newTicket.gallonsDelivered;
    }

    return newTicket;
};

export const maxValidFill = (device: Device) => {
    if (!device) return NaN;

    const capacity = device.capacity;
    const tankPctEmpty = (100 - (device.estimated_tank ?? 20)) / 100; // assume 20% full since that's what we base estimates on

    if (device.fuelType === 'propane') {
        return Number((capacity * 0.8 * tankPctEmpty).toFixed(2));
    } else {
        // oil
        return Number((capacity * tankPctEmpty).toFixed(2));
    }
};

export const searchDeliveries = (
    deliveries: Ticket[],
    searchTerm: string,
    devices: DeviceLookup,
    userId?: string
) => {
    return deliveries.filter((ticket) => {
        const device = devices[ticket.deviceId] ?? {};
        if (_.isEmpty(device)) {
            return false;
        }

        const stringToSearch =
            ticket.name +
            device.name +
            device.shortDeviceId +
            device.customer_name +
            device.address;
        if (!stringToSearch) {
            return true;
        }

        const searchMatch = stringToSearch
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        if (userId) {
            const idMatch = ticket.taggedUsers?.[0]?.uid === userId;
            return idMatch && searchMatch;
        }

        return searchMatch;
    });
};

export const filterDeliveriesByStatus = (
    filters: Record<string, boolean>,
    deliveries: Ticket[]
): Ticket[] => {
    const onFilters = Object.entries(filters)
        .filter((entry) => entry[1] === true)
        .map((entry) => entry[0]);

    const numberOfFilters = Object.keys(filters).length;
    if (onFilters.length === 0 || onFilters.length === numberOfFilters)
        return deliveries;

    const filteredDeliveries = deliveries.filter((ticket: Ticket) => {
        if (!ticket.status?.toLowerCase) {
            return true;
        }

        return onFilters.includes(ticket.status?.toLowerCase());
    });

    return filteredDeliveries;
};

// TODO: may need to add a type to data
export const constructTicket = (data: any) => {
    const { fillup, plannedDate, selectedDriver, ...manualData } = data;
    const newTicket: Partial<Ticket> = {
        ...manualData,
        ticketCategory: 'delivery',
        ticketTrigger: 'fill',
        status: 'backlog',
        sleep: {
            status: false,
            days: '0',
        },
        ticketType: 'fill',
    };

    if (plannedDate) {
        newTicket.plannedDate = new Date(plannedDate);
    }

    if (selectedDriver) {
        newTicket.taggedUsers = [selectedDriver];
        newTicket.status = 'planned';
    }

    if (fillup) {
        newTicket.lastFillup = fillup;
    }

    return newTicket;
};
